// ===== ROUTE PATHS =====
export const ROUTE_PATHS = {
  HOME: '/',
  BLOG: '/blog',
  BLOG_POST: '/blog/:slug',
} as const;
// ===== SOCIAL LINKS =====
export const SOCIAL_LINKS = {
  WHATSAPP: 'https://wa.me/message/GQHHPYO4I6NPC1',
  LINKEDIN: 'https://www.linkedin.com/in/kamillo-santos-2b97b915b?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app',
  INSTAGRAM: 'https://www.instagram.com/kamillosantos',
  YOUTUBE: 'https://www.youtube.com/@kamillosantos',
  EMAIL: 'gestordetrafegokamillo@gmail.com',
} as const;

// ===== TYPES =====
export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  date: string;
  readTime: string;
  tags: string[];
}

export interface Metric {
  value: string;
  label: string;
  description: string;
}

export interface Service {
  icon: string;
  title: string;
  description: string;
  features: string[];
}

// ===== BLOG DATA =====
export const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'roi-100x-ia-preditiva',
    title: 'ROI 100x é Real: Como a IA Preditiva Transformou Meu Approach em Tráfego Pago',
    excerpt: 'Abandonei o "teste e erre" tradicional. Descubra como a modelagem Bayesiana e o Neural Bidding entregam resultados que o mercado considera impossíveis.',
    content: `
## O Fim da Era do "Testar Criativo"

Em 2024, eu ainda acreditava no ciclo clássico: criar criativos, testar com pequenos orçamentos, escalar o que funciona. Parecia lógico. Era lento, impreciso e caríssimo.

A virada aconteceu quando comecei a trabalhar com **Modelagem Bayesiana aplicada ao comportamento do usuário**. Em vez de esperar os dados do passado para tomar decisões, passei a *predizer* o comportamento futuro antes do clique.

## O Protocolo Nexus Maximus em 4 Etapas

### 1. Captura Total de Dados (Server-Side)
O primeiro problema real do tráfego pago moderno? **Perda de dados**. Bloqueadores de anúncios, restrições de cookies e limitações de privacidade destroem até 40% dos seus dados de conversão quando você usa pixel client-side.

Com Server-Side Tagging, capturamos **100% dos eventos** diretamente do servidor, sem depender do navegador do usuário. Isso sozinho já muda completamente a qualidade dos dados que alimentam os algoritmos.

### 2. Neural Bidding com Reinforcement Learning
O Google Ads e Meta Ads têm seus próprios algoritmos de lance automático. Mas eles otimizam para o **menor custo por conversão** — não para o **maior lucro líquido**.

Nosso modelo de Reinforcement Learning aprende continuamente qual tipo de usuário tem o maior LTV (Lifetime Value), e ajusta os lances para maximizar receita real, não apenas volume de conversões.

### 3. Previsão Bayesiana de Escala
Antes de aumentar o orçamento, nosso sistema calcula a **probabilidade de sucesso futuro** com base em:
- Histórico de performance do segmento
- Sazonalidade preditiva
- Comportamento do algoritmo nas últimas 72h
- Saturação do público-alvo

### 4. Valorização e Loop de Feedback
Cada resultado alimenta o modelo. O sistema fica mais preciso a cada ciclo. Após 30 dias, a precisão preditiva ultrapassa os **95%**.

## Resultados Reais

Para um cliente no setor automotivo em Salvador, saímos de um CPL (Custo por Lead) de R$ 87 para R$ 12 em 45 dias. ROI passou de 3x para 47x no mesmo período.

## Conclusão

O marketing digital que você conheceu antes de 2025 morreu. A escala não vem do esforço — vem da **precisão dos dados e da inteligência do sistema**.

Se você ainda está testando criativos manualmente, está literalmente deixando dinheiro na mesa.
    `,
    category: 'IA Preditiva',
    date: '2026-03-05',
    readTime: '8 min',
    tags: ['IA Preditiva', 'ROI', 'Neural Bidding', 'Bayesiana', 'Tráfego Pago'],
  },
  {
    slug: 'server-side-tagging-guia-completo',
    title: 'Server-Side Tagging: O Guia Definitivo para Não Perder Mais Dados',
    excerpt: 'Bloqueadores de anúncio e restrições de cookies estão destruindo seus dados. Veja como o rastreamento server-side resolve esse problema de uma vez por todas.',
    content: `
## Por Que Seus Dados Estão Mentindo Para Você

Se você usa pixel client-side (Google Tag, Meta Pixel, etc.), saiba: você está vendo **apenas 60-70% das conversões reais**. O resto some por causa de:

- **Ad Blockers**: uBlock Origin, AdBlock Plus bloqueiam tags de terceiros
- **ITP (Intelligent Tracking Prevention)**: Safari limita cookies de terceiros a 7 dias
- **GDPR/LGPD**: usuários que rejeitam cookies não são rastreados
- **Latência de rede**: tags que falham por timeout

## A Solução: Server-Side Tagging

Em vez de o navegador do usuário enviar dados diretamente para o Google/Meta, **seu servidor age como intermediário**:

\`\`\`
Usuário → Seu Site → SEU SERVIDOR → Google / Meta / TikTok
\`\`\`

Como é seu servidor confiável, não há bloqueadores que possam interceptar. Os dados chegam 100% íntegros.

## Implementação com Google Tag Manager Server-Side

### Passo 1: Criar o Container Server-Side no GTM
No Google Tag Manager, crie um novo container do tipo "Server". Você receberá um URL de endpoint (ex: \`https://analytics.seusite.com.br\`).

### Passo 2: Configurar o Servidor de Tagging
Recomendo usar **Google Cloud Run** para o container GTM Server. É serverless, escala automaticamente e custa poucos reais por mês para a maioria das implementações.

### Passo 3: Redirecionar o Pixel para Seu Servidor
Ao invés de carregar \`gtm.js\` do Google, você carrega do seu próprio endpoint:

\`\`\`javascript
// Antes (client-side)
<script src="https://www.googletagmanager.com/gtm.js?id=GTM-XXXXXX">

// Depois (server-side)  
<script src="https://analytics.seusite.com.br/gtm.js?id=GTM-XXXXXX">
\`\`\`

## Resultados Esperados

Clientes que migraram para server-side tagging reportam:
- **+30-45%** de aumento nas conversões rastreadas
- **-25%** de queda no CPA aparente (os dados eram imprecisos antes)
- **+60%** de precisão nos públicos de remarketing

## Conclusão

Server-side tagging não é mais opcional — é a fundação de qualquer estratégia séria de dados em 2026.
    `,
    category: 'Engenharia de Dados',
    date: '2026-02-20',
    readTime: '10 min',
    tags: ['Server-Side', 'GTM', 'Rastreamento', 'Dados', 'LGPD'],
  },
  {
    slug: 'framework-kjs-salvador',
    title: 'O Framework KJS: Como Dominamos o Tráfego Local em Salvador',
    excerpt: 'Pizzarias, concessionárias, hotéis — cada nicho de Salvador tem padrões de dados únicos. O Framework KJS transforma esses padrões em sistemas de venda previsíveis.',
    content: `
## Salvador Não É São Paulo

Um erro clássico: aplicar estratégias de tráfego desenvolvidas para grandes centros em cidades menores como Salvador.

Salvador tem suas particularidades:
- Sazonalidade fortemente ligada ao Carnaval e festividades baianas
- Alta concentração de consumo em bairros específicos (Barra, Pituba, Rio Vermelho)
- Comportamento de compra com forte influência social e indicação
- Ticket médio diferente dependendo da zona sul/norte

O Framework KJS (Kamillo JS) foi desenvolvido especificamente para o contexto soteropolitano.

## Os 3 Pilares do Framework KJS

### 1. Geofencing Inteligente para Restaurantes e Pizzarias
Para delivery e restaurantes, o raio de entrega é tudo. Usamos geofencing dinâmico que considera:

- **Horários de pico**: configuramos campanhas que aumentam automaticamente o orçamento nas 30 minutos antes dos picos de fome (12h e 19h)
- **Clima**: em dias quentes, pizza e delivery aumentam 40% — nossa IA detecta previsão climática e ajusta orçamento
- **Eventos locais**: shows, jogos do Bahia/Vitória → impulso de delivery nas regiões próximas

### 2. Funil Invisível para Hotéis e Turismo
Turistas planejam viagens com antecedência. Nossa estratégia:

1. **Fase de Consideração** (60-90 dias antes): anúncios de brand awareness para quem pesquisa "Salvador Bahia o que fazer"
2. **Fase de Intenção** (30 dias antes): remarketing para quem visitou páginas de preços e disponibilidade
3. **Fase de Decisão** (7 dias antes): ofertas exclusivas com senso de urgência

### 3. Filtro de Intenção para Veículos
Para concessionárias, a diferença entre leads bons e ruins é enorme:

| Keyword | Intenção | Ação |
|---------|----------|------|
| "fotos de carros" | Curiosidade | Excluir |
| "qual carro comprar" | Pesquisa | Nutrir |
| "financiamento carro Salvador" | Compra | Priorizar |
| "comprar Honda HRB 2026" | Alta conversão | Máximo orçamento |

## Métricas Reais em Salvador

Após 6 meses com o Framework KJS:
- **Pizzaria na Pituba**: Custo por pedido caiu de R$ 15 para R$ 4,20
- **Hotel na Barra**: Ocupação aumentou 35% nas baixas temporadas
- **Concessionária no Iguatemi**: CPL de R$ 87 para R$ 23 com qualidade 3x maior

## Conclusão

Dados locais + IA preditiva = dominação do mercado soteropolitano. O Framework KJS não é teoria — é um sistema testado e comprovado nas ruas de Salvador.
    `,
    category: 'Tráfego Local',
    date: '2026-02-08',
    readTime: '7 min',
    tags: ['Framework KJS', 'Salvador', 'Tráfego Local', 'Geofencing', 'Restaurantes'],
  },
  {
    slug: 'neural-bidding-google-ads',
    title: 'Neural Bidding: Por Que o Smart Bidding do Google Não É Suficiente',
    excerpt: 'O algoritmo do Google otimiza para conversões, não para lucro. Veja como o Neural Bidding com Reinforcement Learning vai além e maximiza sua receita real.',
    content: `
## O Problema do Smart Bidding Padrão

O Google Smart Bidding é poderoso. Mas tem uma limitação fundamental: ele foi projetado para maximizar **volume de conversões** ao menor custo.

O problema? Nem toda conversão vale o mesmo.

Um lead de uma pizzaria pode valer R$ 50 ou R$ 500 dependendo do histórico de compras do cliente. O Smart Bidding padrão trata os dois da mesma forma.

## Neural Bidding: Otimização por Valor Real

O Neural Bidding usa Reinforcement Learning para aprender, em tempo real, qual usuário tem maior probabilidade de gerar **maior receita** — não apenas de converter.

## Como Funciona na Prática

O modelo coleta sinais que o Google não usa nativamente:
- CRM data (histórico de compras)
- Comportamento pós-conversão
- Valor médio de pedido por segmento
- Taxa de churn por perfil

## Resultados

Em 90 dias de implementação:
- **+280% de receita** com mesmo orçamento
- **-40% de CPA** efetivo
- **3x mais conversões de alto valor**

A diferença entre Smart Bidding e Neural Bidding é a diferença entre jogar xadrez com as peças cegas e jogar vendo o tabuleiro inteiro.
    `,
    category: 'Google Ads',
    date: '2026-01-22',
    readTime: '6 min',
    tags: ['Neural Bidding', 'Google Ads', 'Smart Bidding', 'Reinforcement Learning', 'ROI'],
  },
  {
    slug: 'modelagem-bayesiana-marketing',
    title: 'Modelagem Bayesiana no Marketing: Prevendo o Futuro com Probabilidade',
    excerpt: 'Como substituir o "achismo" por probabilidade matemática para tomar decisões de escala mais inteligentes e reduzir o risco ao investir em tráfego pago.',
    content: `
## De Adivinhação a Probabilidade

A maioria dos gestores de tráfego escala quando uma campanha "parece estar indo bem". Isso é intuição disfarçada de estratégia.

A Modelagem Bayesiana substitui o "parecer" por uma pergunta precisa: **dada toda a evidência disponível, qual é a probabilidade de que aumentar o orçamento em 50% gerará proporcionalmente mais receita?**

## O Teorema de Bayes Aplicado ao Tráfego

A fórmula básica:

**P(sucesso | dados) = P(dados | sucesso) × P(sucesso) / P(dados)**

Na prática:
- **P(sucesso)**: probabilidade prévia baseada em campanhas similares
- **P(dados | sucesso)**: quão prováveis são os dados observados SE a campanha for bem-sucedida
- **P(sucesso | dados)**: probabilidade atualizada de sucesso dado o que observamos

## Como Aplicamos na Prática

Para cada decisão de escala, calculamos três cenários:
1. **Cenário Pessimista** (10% de probabilidade): desempenho cai 30%
2. **Cenário Base** (70% de probabilidade): desempenho mantém
3. **Cenário Otimista** (20% de probabilidade): desempenho melhora 20%

Valor esperado = -30% × 0.1 + 0% × 0.7 + 20% × 0.2 = **+1%**

Se o valor esperado for positivo, escalamos. Simples, mas poderoso.

## Resultados

Clientes que adotaram tomada de decisão Bayesiana reduziram o "desperdício de orçamento" em teste em **65%** e aumentaram a eficiência de escala em **45%**.

Menos risco. Mais resultado. Isso é matemática a serviço do marketing.
    `,
    category: 'Ciência de Dados',
    date: '2026-01-10',
    readTime: '9 min',
    tags: ['Bayesiana', 'Ciência de Dados', 'Escala', 'Probabilidade', 'Tráfego Pago'],
  },
  {
    slug: 'protocolo-kamillo-2026',
    title: 'Protocolo Kamillo 2026: O Sistema Completo de Performance de Elite',
    excerpt: 'Uma visão geral do sistema proprietário que une Server-Side Tagging, IA Causal e Modelagem Bayesiana para entregar ROI Preditivo com 95% de precisão.',
    content: `
## A Gênese do Protocolo

Depois de gerenciar mais de R$ 50 milhões em verba de tráfego pago ao longo de 8 anos, identifiquei um padrão nos casos de alto ROI: **todos tinham infraestrutura de dados superior**.

Não era o criativo. Não era o orçamento. Era a qualidade e a inteligência dos dados.

O Protocolo Kamillo 2026 é a síntese de tudo que aprendi sobre como construir essa infraestrutura.

## Os 4 Pilares

### Pilar 1: Captura Perfeita
- Server-Side Tagging para zero perda de dados
- Enhanced Conversions (Google) + CAPI (Meta)
- UTM tracking estruturado com modelo de atribuição customizado

### Pilar 2: Inteligência Preditiva  
- Modelos de Machine Learning treinados com dados históricos do cliente
- Previsão de sazonalidade com 90 dias de antecedência
- Segmentação preditiva por LTV

### Pilar 3: Ativação Otimizada
- Neural Bidding para máximo ROI
- Audiências dinâmicas atualizadas em tempo real
- Creative Intelligence para identificar fadiga de anúncios antes que afete o CPM

### Pilar 4: Ciclo de Valorização Contínua
- Dashboard em tempo real com alertas inteligentes
- Relatórios mensais com análise causal (não apenas correlação)
- Reuniões de estratégia com tomada de decisão baseada em dados

## Para Quem É o Protocolo

O Protocolo Kamillo 2026 é para empresas que:
- Investem mínimo de R$ 30.000/mês em mídia paga
- Têm dados históricos de pelo menos 6 meses
- Querem escalar com previsibilidade, não com esperança

Se você se encaixa, entre em contato. Vamos construir sua máquina de crescimento.
    `,
    category: 'Protocolo Kamillo',
    date: '2026-01-02',
    readTime: '11 min',
    tags: ['Protocolo Kamillo', 'Performance', 'IA Causal', 'Server-Side', 'ROI Preditivo'],
  },
];

// ===== METRICS =====
export const METRICS: Metric[] = [
  { value: '95%', label: 'Precisão Preditiva', description: 'do Protocolo Kamillo 2026' },
  { value: 'ROI 100x', label: 'Resultado Máximo', description: 'entregue com IA Preditiva' },
  { value: '+R$50M', label: 'Verba Gerenciada', description: 'em tráfego pago ao longo da carreira' },
  { value: '8+', label: 'Anos de Experiência', description: 'em performance e engenharia de dados' },
];

// ===== CPAV STEPS =====
export const CPAV_STEPS = [
  {
    letter: 'C',
    title: 'Captura',
    description: 'Implementação de rastreamento Server-Side perfeito. Capturamos cada sinal sem perda de dados por cookies ou bloqueadores.',
    icon: '📡',
  },
  {
    letter: 'P',
    title: 'Processamento',
    description: 'Modelagem de dados em tempo real. Nossa IA processa o comportamento do usuário e prediz o LTV antes mesmo do clique.',
    icon: '⚡',
  },
  {
    letter: 'A',
    title: 'Ativação',
    description: 'Neural Bidding com Reinforcement Learning. Lances inteligentes que buscam o lucro real, não apenas o menor custo.',
    icon: '🎯',
  },
  {
    letter: 'V',
    title: 'Valorização',
    description: 'ROI exponencial com previsão Bayesiana. Ajustamos a escala baseada na probabilidade de sucesso futuro.',
    icon: '📈',
  },
];

// ===== SERVICES =====
export const SERVICES: Service[] = [
  {
    icon: '🏪',
    title: 'Restaurantes & Pizzarias',
    description: 'Geofencing inteligente que captura o lead no momento exato da fome, usando dados de horário de pico e raio de entrega.',
    features: ['Geofencing dinâmico', 'Lances por horário de pico', 'Integração com delivery apps', 'ROI médio: 12x'],
  },
  {
    icon: '🏨',
    title: 'Hotéis & Turismo',
    description: 'Funil Invisível que cerca o turista desde a pesquisa inicial até a reserva, aproveitando calendário de eventos e sazonalidade.',
    features: ['Funil de 3 fases', 'Remarketing inteligente', 'Integração com OTAs', 'Ocupação +35%'],
  },
  {
    icon: '🚗',
    title: 'Concessionárias & Veículos',
    description: 'Filtro de intenção que separa curiosos de compradores, focando orçamento em quem realmente vai fechar negócio.',
    features: ['Filtro de intenção avançado', 'Qualificação de leads', 'Integração com CRM', 'CPL -75%'],
  },
  {
    icon: '🛍️',
    title: 'Lojas & Varejo',
    description: 'Estratégia omnichannel que conecta presença online e tráfego físico para maximizar vendas no ponto de venda.',
    features: ['Campanhas omnichannel', 'Store visit tracking', 'Audience lookalike', 'Conversão +40%'],
  },
];

// ===== UTILS =====
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
}

export function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    'IA Preditiva': 'text-primary border-primary/30 bg-primary/10',
    'Engenharia de Dados': 'text-accent border-accent/30 bg-accent/10',
    'Tráfego Local': 'text-[oklch(0.70_0.22_38)] border-[oklch(0.70_0.22_38)]/30 bg-[oklch(0.70_0.22_38)]/10',
    'Google Ads': 'text-primary border-primary/30 bg-primary/10',
    'Ciência de Dados': 'text-accent border-accent/30 bg-accent/10',
    'Protocolo Kamillo': 'text-[oklch(0.70_0.22_38)] border-[oklch(0.70_0.22_38)]/30 bg-[oklch(0.70_0.22_38)]/10',
  };
  return colors[category] || 'text-primary border-primary/30 bg-primary/10';
}
